import * as React from "react";
import { RefObject } from "react";
import ReportStatus from "../commons/model/report/ReportStatus";
import PagedModel from "../commons/components/pagination/PagedModel";
import Column, { Enum } from "../commons/components/pagination/Column";
import Sort from "../commons/components/pagination/Sort";
import PaginatedList from "../PaginatedList";
import ReportStatusUtils from "./ReportStatusUtils";
import ReportFragment, { isAccessible, isClickable } from "./ReportFragment";
import ReportTypeUtils from "./ReportTypeUtils";
import FlowType from "../commons/model/FlowType";

export interface ReportListProps {
  page: PagedModel<ReportFragment>;
  columns: Column[];
  troubleshootUrlFactory?: (report: ReportFragment) => string;
  fetchUrl: string;
  sort: Sort;
  onRefresh?: () => void;
  disableFiltering?: boolean;
  classNames?: string;
}

export default class ReportList extends React.Component<ReportListProps> {
  constructor(props: ReportListProps) {
    super(props);
    ReportList.onReportClick = ReportList.onReportClick.bind(this);
    this.listRef = React.createRef();
  }

  private listRef: RefObject<PaginatedList>;

  render() {
    const columns = this.props.columns;
    const patientColumn = columns.find((it) => it.attribute === "REPORT_PATIENT_NAME");
    if (patientColumn) {
      patientColumn.renderer = this.getPatientComponent;
    }

    const statusColumn = columns.find((it) => it.attribute === "REPORT_STATUS") as Enum & Column;
    if (statusColumn) {
      statusColumn.values = Object.keys(ReportStatus).reduce((previousValue, currentValue) => {
        previousValue[currentValue] =
          i18n[ReportStatusUtils.getStatusTypeI18nKey(ReportStatus[currentValue])];
        return previousValue;
      }, {});
      statusColumn.renderer = (report: ReportFragment) =>
        ReportStatusUtils.getStatusComponent({
          status: report.statusInfo.status,
          rejectionCause: report.statusInfo.rejectionCause,
          technicalErrorMsg: report.technicalErrorMsg,
        });
    }

    const actionColumn = columns.find((it) => it.path === "action");
    if (actionColumn) {
      actionColumn.renderer = this.getActionComponent.bind(this);
    }

    const reportTypeColumn = columns.find((it) => it.attribute === "REPORT_TYPE") as Enum & Column;
    if (reportTypeColumn) {
      reportTypeColumn.values = Object.keys(FlowType).reduce((previousValue, currentValue) => {
        previousValue[currentValue] =
          i18n[ReportTypeUtils.getReportTypeI18nKey(FlowType[currentValue])];
        return previousValue;
      }, {});
      reportTypeColumn.renderer = (report: ReportFragment) =>
        ReportTypeUtils.getTypeComponent(report.reportType);
    }

    return (
      <PaginatedList
        ref={this.listRef}
        initialPage={this.props.page}
        itemKey="uid"
        columns={columns}
        emptyListMessage={i18n["Page.Report.Empty"]}
        rowOnClick={ReportList.onReportClick}
        fetchUrl={this.props.fetchUrl}
        onUpdate={this.componentDidUpdate}
        rowClasses={(reportFragment: ReportFragment) =>
          isClickable(reportFragment) ? "" : "disabled"
        }
        additionalFilterQueries={[]}
        initialSort={this.props.sort}
        disableFiltering={this.props.disableFiltering}
        classNames={this.props.classNames}
      />
    );
  }

  private static onReportClick(report: ReportFragment) {
    if (isClickable(report)) {
      if (
        [
          ReportStatus.AVAILABLE,
          ReportStatus.SHARED_WITH_PATIENT,
          ReportStatus.VIEWED_BY_PATIENT,
        ].includes(report.statusInfo.status)
      ) {
        document.location.href = "/reports/" + report.uid;
      } else {
        document.location.href = "/reports/" + report.uid + "/troubleshoot";
      }
    }
  }

  componentDidUpdate() {
    window["verticalDropdownUtil"] && window["verticalDropdownUtil"].reAttach();
  }

  private getPatientComponent = (report: ReportFragment) => (
    <>
      {report.subjectName}
      {isAccessible(report) && !report.read && <span className="badge">{i18n["Global.New"]}</span>}
    </>
  );

  private getActionComponent(report: ReportFragment) {
    return null;
  }
}
